/** Points gained or lost per factor key over the trend window. */
export type FactorTrends = Record<string, number>;

/**
 * The window arrives from the URL via `qs.parse`, which never coerces, so the
 * bounds are strings in practice however they are typed upstream.
 */
interface TrendWindow {
  readonly before?: number | string;
  readonly after?: number | string;
}

const SECONDS_PER_DAY = 24 * 60 * 60;

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Normalizes a unix-seconds bound that may have come through the URL as a
 * string. `Number('')` is 0 and `Number(null)` is 0, so both are rejected
 * explicitly rather than being read as the epoch.
 */
export const toEpochSeconds = (value: unknown): number | undefined => {
  if (isNumber(value)) {
    return value;
  }
  if (typeof value !== 'string' || value.trim() === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const readSeries = (entry: unknown): number[] => {
  const buckets = (entry as { buckets?: unknown })?.buckets;
  if (!Array.isArray(buckets)) {
    return [];
  }
  // Gaps in the history come back as null readings; dropping them keeps a
  // sparse series usable instead of discarding the factor's whole trend.
  return buckets
    .map((bucket) => (bucket as { value?: unknown })?.value)
    .filter(isNumber);
};

/**
 * Reduces the risk trend response (`api/risk/v1/trend/:accountId`) to one
 * delta per factor: the movement between the first and last reading in the
 * window. Factors without at least two readings are omitted, so callers can
 * distinguish "no history" from "flat".
 */
export function buildFactorTrends(data: unknown): FactorTrends {
  if (!data || typeof data !== 'object') {
    return {};
  }

  return Object.entries(data as Record<string, unknown>).reduce(
    (trends: FactorTrends, [factorKey, entry]) => {
      const series = readSeries(entry);
      if (series.length < 2) {
        return trends;
      }
      return { ...trends, [factorKey]: series[series.length - 1] - series[0] };
    },
    {}
  );
}

/**
 * Describes the window the deltas were measured over. The dashboard's range is
 * user-adjustable and often absent from the URL entirely, so the label is
 * derived from the data -- and omitted rather than guessed, because a vague
 * period is worse than none next to a precise point delta.
 */
export function formatTrendWindow(window: TrendWindow): string {
  const before = toEpochSeconds(window.before);
  const after = toEpochSeconds(window.after);

  if (before === undefined || after === undefined || before <= after) {
    return '';
  }
  const days = Math.ceil((before - after) / SECONDS_PER_DAY);
  return `in the past ${days} ${days === 1 ? 'day' : 'days'}`;
}
